import React from 'react';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import TextInput from '@grnhse/seedling/lib/birch/components/forms/text_input';
import { SecondaryBody } from '@grnhse/seedling/lib/birch/typography';
import InlineLabel from 'shared/components/birch/inline_label';
import { HelpTooltip } from '@grnhse/seedling/lib/birch/components/tooltips';
import KeywordForm from 'talent_search/components/keyword_form';
import type { FilterType } from 'talent_search/shared/types';
import { CompanyMode } from 'talent_matching/types';
import { withLocalePrefix } from 'shared/utils/translation';
import { type ExperienceState } from 'talent_matching/components/filters/use_experience_state';
import DoesNotHaveLabel, {
  doesNotHaveConnector,
} from 'talent_matching/components/filters/does_not_have_label';

const t = withLocalePrefix('talent_matching.filter_row.experience');

type CompanySection = {
  mode: CompanyMode;
  // Borrowed from the keyword filter's chip input; only scopes its data-provides hooks.
  filterType: FilterType;
  label: string;
  description: string;
  booleanIndicator: string;
};

const INCLUDE_SECTIONS: CompanySection[] = [
  {
    mode: 'can_have',
    filterType: 'can_have',
    label: t('can_have'),
    description: t('can_have_description'),
    booleanIndicator: t('or'),
  },
  {
    mode: 'include',
    filterType: 'must_have',
    label: t('must_have'),
    description: t('must_have_description'),
    booleanIndicator: t('and'),
  },
];

type ExperienceContentProps = {
  state: ExperienceState;
  // Discovery hides this: AdeptID filters on years of experience but has no
  // employer filter, so company sections there would narrow nothing.
  showCompanyExperience?: boolean;
};

export const ExperienceContent = ({
  state,
  showCompanyExperience = true,
}: ExperienceContentProps) => {
  const {
    companyValues,
    companyExcludesMatch,
    setCompanyExcludesMatch,
    minValue,
    maxValue,
    setMinValue,
    setMaxValue,
    handleCompaniesChange,
    handleRemoveCompany,
  } = state;

  return (
    <Stack spacing={3} direction="column" sx={{ width: '100%' }}>
      <Stack spacing={1} sx={{ width: '100%' }}>
        <SecondaryBody medium>{t('description')}</SecondaryBody>
        <Stack spacing={1} direction="row" sx={{ width: '100%' }}>
          <TextInput
            label={t('minimum')}
            value={minValue}
            onChange={(e) => setMinValue(e.target.value)}
            type="number"
            min={0}
            max={100}
          />
          <TextInput
            label={t('maximum')}
            value={maxValue}
            onChange={(e) => setMaxValue(e.target.value)}
            type="number"
            min={0}
            max={100}
          />
        </Stack>
      </Stack>

      {showCompanyExperience && (
        <Stack spacing={1} sx={{ width: '100%' }}>
          <SecondaryBody medium>{t('company_experience')}</SecondaryBody>
          <Stack spacing={3} sx={{ width: '100%' }}>
            {INCLUDE_SECTIONS.map((section) => (
              <Stack key={section.mode} spacing={1} sx={{ width: '100%' }}>
                <Stack spacing={1} direction="row" alignItems="center">
                  <InlineLabel>{section.label}</InlineLabel>
                  <HelpTooltip
                    label={section.description}
                    ariaLabel={section.description}
                  />
                </Stack>
                <KeywordForm
                  filterType={section.filterType}
                  values={companyValues[section.mode]}
                  onAdd={(values) =>
                    handleCompaniesChange(section.mode, values)
                  }
                  onRemove={(value) => handleRemoveCompany(section.mode, value)}
                  booleanIndicator={section.booleanIndicator}
                  pageName="search"
                />
              </Stack>
            ))}
            <Stack spacing={1} sx={{ width: '100%' }}>
              <DoesNotHaveLabel
                match={companyExcludesMatch}
                onMatchChange={setCompanyExcludesMatch}
              />
              <KeywordForm
                filterType="does_not_have"
                values={companyValues.exclude}
                onAdd={(values) => handleCompaniesChange('exclude', values)}
                onRemove={(value) => handleRemoveCompany('exclude', value)}
                booleanIndicator={doesNotHaveConnector(companyExcludesMatch)}
                pageName="search"
              />
            </Stack>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};
